import { useState, useEffect, useRef } from "react";

// ─── THEME — Rik.ai brand palette (shared with App.js) ────────────────────────
const C = {
  bg: "#06080F", surface: "#0F1117", card: "#131520", border: "#1E2231",
  accent: "#8B5CF6", accentGlow: "#8B5CF630", teal: "#22D3EE", tealGlow: "#22D3EE20",
  blue: "#3B82F6", amber: "#F59E0B", red: "#EF4444", text: "#FFFFFF", muted: "#94A3B8", subtle: "#1E2231",
  purple: "#8B5CF6",
};

const API_BASE   = process.env.REACT_APP_API_URL || "";
const LEGAL_BASE = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:3001" : "");
const LEGAL_PP   = `${LEGAL_BASE}/api/legal/participant/privacy_policy/page`;
const LEGAL_TOS  = `${LEGAL_BASE}/api/legal/participant/terms_of_service/page`;

// ─── STUB DATA ────────────────────────────────────────────────────────────────
// In production: all stubs replaced by real API calls to your backend.

const INDIA_CITIES = [
  // Tier 1
  { name:"Mumbai",     tier:1 }, { name:"Delhi",       tier:1 }, { name:"Bengaluru",  tier:1 },
  { name:"Chennai",    tier:1 }, { name:"Kolkata",     tier:1 }, { name:"Hyderabad",  tier:1 },
  { name:"Pune",       tier:1 }, { name:"Ahmedabad",   tier:1 },
  // Tier 2
  { name:"Jaipur",     tier:2 }, { name:"Lucknow",     tier:2 }, { name:"Kochi",      tier:2 },
  { name:"Chandigarh", tier:2 }, { name:"Indore",      tier:2 }, { name:"Nagpur",     tier:2 },
  { name:"Bhopal",     tier:2 }, { name:"Patna",       tier:2 }, { name:"Vadodara",   tier:2 },
  { name:"Coimbatore", tier:2 }, { name:"Visakhapatnam",tier:2},
  // Tier 3
  { name:"Agra",       tier:3 }, { name:"Varanasi",    tier:3 }, { name:"Rajkot",     tier:3 },
  { name:"Madurai",    tier:3 }, { name:"Mysuru",      tier:3 }, { name:"Surat",      tier:3 },
  { name:"Other",      tier:3 },
];

// Language list driven at runtime from /api/config — passed as prop from App.js

const PROFESSIONS = [
  "Student","Salaried — Private Sector","Salaried — Government / PSU",
  "Business Owner / Self Employed","Freelancer / Consultant",
  "Homemaker","Retired","Healthcare Professional",
  "IT / Tech Professional","Finance / Banking","Other",
];

const EDUCATION_LEVELS = [
  "High School or below","Diploma / Vocational","Graduate (Bachelor's)",
  "Post Graduate (Master's)","Doctorate / PhD","Other",
];

const INCOME_BANDS = [
  "Prefer not to say","Below ₹3 LPA","₹3–6 LPA","₹6–10 LPA",
  "₹10–20 LPA","₹20–40 LPA","Above ₹40 LPA",
];

const HOUSEHOLD_ROLES = [
  "Primary decision maker","Joint decision maker","Influencer (input but not final call)","Not involved in household decisions",
];

const DEVICE_USAGE = ["Smartphone only","Laptop / Desktop only","Both smartphone and laptop/desktop","Tablet primarily"];

// Profile survey sections — each section = one "card" screen in the wizard
const PROFILE_SECTIONS = [
  {
    id: "education",
    icon: "🎓",
    title: "Education & Profession",
    subtitle: "Helps us match you to the right surveys",
    fields: [
      { key:"education",      label:"Highest education level",   type:"select",  options:EDUCATION_LEVELS, required:true  },
      { key:"profession",     label:"What best describes you?",  type:"select",  options:PROFESSIONS,      required:true  },
      { key:"yearsWorking",   label:"Years of work experience",  type:"select",  options:["Less than 1 year","1–3 years","3–7 years","7–15 years","15+ years","Not applicable"], required:true },
    ],
  },
  {
    id: "income",
    icon: "💰",
    title: "Financial Profile",
    subtitle: "Income is optional — skip if you prefer",
    fields: [
      { key:"incomeBand",     label:"Annual household income",   type:"select",  options:INCOME_BANDS,     required:false },
      { key:"householdRole",  label:"Your role in household purchase decisions", type:"select", options:HOUSEHOLD_ROLES, required:true },
      { key:"dependents",     label:"Number of dependents",      type:"select",  options:["0","1","2","3","4","5+"],          required:true },
    ],
  },
  {
    id: "purchases",
    icon: "🛒",
    title: "Purchase Behaviour",
    subtitle: "How you shop tells us a lot",
    fields: [
      { key:"onlineFreq",     label:"How often do you shop online?",             type:"select", options:["Daily","Multiple times a week","Weekly","Monthly","Rarely","Never"], required:true },
      { key:"avgSpend",       label:"Avg. monthly online spend",                 type:"select", options:["Below ₹1,000","₹1,000–5,000","₹5,000–15,000","₹15,000–30,000","Above ₹30,000","Prefer not to say"], required:true },
      { key:"primaryChannel", label:"Where do you mostly shop?",                 type:"multiselect", options:["Amazon","Flipkart","Meesho","Myntra","D2C brand websites","Physical retail stores","Social commerce","Other"], required:true },
    ],
  },
  {
    id: "tech",
    icon: "📱",
    title: "Technology & Media",
    subtitle: "Your digital habits help us design better surveys",
    fields: [
      { key:"deviceUsage",    label:"Primary device(s) you use",                 type:"select",      options:DEVICE_USAGE, required:true },
      { key:"internetHours",  label:"Daily internet usage",                      type:"select",      options:["Less than 1 hour","1–3 hours","3–6 hours","6–10 hours","10+ hours"], required:true },
      { key:"socialMedia",    label:"Social platforms you use regularly",        type:"multiselect", options:["Instagram","YouTube","WhatsApp","Facebook","Twitter/X","LinkedIn","Snapchat","ShareChat","None"], required:true },
      { key:"contentType",    label:"Content you consume most",                  type:"multiselect", options:["News","Entertainment / OTT","Sports","Finance / Business","Gaming","Education / How-to","Travel / Lifestyle","Other"], required:true },
    ],
  },
  {
    id: "industry",
    icon: "🏭",
    title: "Industry Familiarity",
    subtitle: "Helps us invite you to relevant surveys",
    fields: [
      { key:"industries",     label:"Industries you follow or work in",          type:"multiselect", options:["FMCG / Consumer Goods","Automobile","Financial Services","Healthcare / Pharma","Real Estate","EdTech / Education","Retail / E-commerce","Travel & Hospitality","Technology / SaaS","Media & Entertainment","Agriculture","Manufacturing","None in particular"], required:true },
      { key:"brandAwareness", label:"How closely do you follow brand news?",     type:"select",      options:["Very closely — I research before every purchase","Somewhat — I notice major launches","Occasionally","Rarely / Not at all"], required:true },
    ],
  },
];

// ─── SHARED UI ────────────────────────────────────────────────────────────────
function GlowDot({ color=C.teal, size=8, pulse=false }) {
  return <span style={{ display:"inline-block", width:size, height:size, borderRadius:"50%", background:color, boxShadow:`0 0 ${size*1.5}px ${color}`, animation:pulse?"pulseDot 2s infinite":"none", flexShrink:0 }} />;
}
function Btn({ children, onClick, variant="primary", size="md", style={}, disabled=false }) {
  const [hov,setHov]=useState(false);
  const vs={ primary:{background:C.accent,color:"#fff",border:"none"}, outline:{background:"transparent",color:C.accent,border:`1px solid ${C.accent}`}, ghost:{background:"transparent",color:C.muted,border:`1px solid ${C.border}`}, teal:{background:C.teal,color:C.bg,border:"none"}, danger:{background:C.red,color:"#fff",border:"none"} };
  const sz={ sm:{fontSize:12,padding:"6px 14px"}, md:{fontSize:13,padding:"9px 20px"}, lg:{fontSize:15,padding:"12px 28px"} };
  return (
    <button onClick={onClick} disabled={disabled} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ borderRadius:8, fontWeight:600, cursor:disabled?"not-allowed":"pointer", fontFamily:"inherit", letterSpacing:"0.01em", opacity:disabled?0.45:hov?0.85:1, transform:hov&&!disabled?"scale(1.02)":"none", transition:"all 0.15s", ...vs[variant], ...sz[size], ...style }}>
      {children}
    </button>
  );
}
function FieldInput({ field, value, onChange, error }) {
  const base = { width:"100%", background:C.surface, border:`1px solid ${error?C.red:C.border}`, borderRadius:8, padding:"11px 14px", color:C.text, fontSize:14, fontFamily:"inherit", boxSizing:"border-box", outline:"none", appearance:"none" };
  const labelSt = { display:"block", color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700, marginBottom:8 };

  if (field.type === "select") {
    return (
      <div style={{ marginBottom:16 }}>
        <label style={labelSt}>{field.label}{!field.required && <span style={{ color:C.muted, fontWeight:400, textTransform:"none", letterSpacing:0, fontSize:11 }}> (optional)</span>}</label>
        <div style={{ position:"relative" }}>
          <select value={value||""} onChange={e=>onChange(e.target.value)}
            style={{ ...base, cursor:"pointer", paddingRight:36 }}
            onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=error?C.red:C.border}>
            <option value="">Select…</option>
            {field.options.map(o=><option key={o} value={o}>{o}</option>)}
          </select>
          <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", color:C.muted, pointerEvents:"none", fontSize:12 }}>▾</span>
        </div>
        {error && <p style={{ color:C.red, fontSize:11, marginTop:4 }}>{error}</p>}
      </div>
    );
  }

  if (field.type === "multiselect") {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      if (selected.includes(opt)) onChange(selected.filter(x=>x!==opt));
      else onChange([...selected, opt]);
    };
    return (
      <div style={{ marginBottom:16 }}>
        <label style={labelSt}>{field.label}{!field.required && <span style={{ color:C.muted, fontWeight:400, textTransform:"none", letterSpacing:0, fontSize:11 }}> (optional)</span>}</label>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {field.options.map(opt => {
            const sel = selected.includes(opt);
            return (
              <button key={opt} onClick={()=>toggle(opt)}
                style={{ padding:"7px 14px", borderRadius:20, fontSize:12, fontWeight:sel?600:400, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s",
                  background:sel?C.accent:C.surface, color:sel?"#fff":C.muted, border:`1px solid ${sel?C.accent:C.border}`,
                  boxShadow:sel?`0 0 10px ${C.accent}50`:"none" }}>
                {opt}
              </button>
            );
          })}
        </div>
        {error && <p style={{ color:C.red, fontSize:11, marginTop:4 }}>{error}</p>}
      </div>
    );
  }

  return null;
}

// ─── STEP INDICATOR ───────────────────────────────────────────────────────────
function StepIndicator({ steps, current }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:0, marginBottom:32 }}>
      {steps.map((s, i) => {
        const done    = i < current;
        const active  = i === current;
        const color   = done ? C.teal : active ? C.accent : C.subtle;
        const txtColor= done ? C.teal : active ? C.accent : C.muted;
        return (
          <div key={i} style={{ display:"flex", alignItems:"center" }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <div style={{ width:32, height:32, borderRadius:"50%", background:done?`${C.teal}20`:active?`${C.accent}20`:C.surface, border:`2px solid ${color}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:txtColor, transition:"all 0.3s" }}>
                {done ? "✓" : i+1}
              </div>
              <span className="reg-step-label" style={{ color:txtColor, fontWeight:active?700:400 }}>{s}</span>
            </div>
            {i < steps.length-1 && (
              <div className="reg-step-connector" style={{ background:done?C.teal:C.subtle }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── STAGE 1: ACCOUNT CREATION ───────────────────────────────────────────────
function StageAccount({ onNext, prefillReferralCode }) {
  const [mobile,   setMobile]   = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [error,    setError]    = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading,  setLoading]  = useState(false);
  // DPDP Act consent — both must be ticked before account creation
  const [consentAge,       setConsentAge]       = useState(false);
  const [consentData,      setConsentData]      = useState(false);
  // Marketing opt-in — pre-ticked, optional
  const [consentMarketing, setConsentMarketing] = useState(true);
  // Referral code — pre-filled from ?r= URL param or typed manually
  const [referralCode,    setReferralCode]    = useState(prefillReferralCode || "");
  const referralLocked = !!prefillReferralCode; // locked if came from URL
  const emailRef = useRef(null);

  const base = { width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"11px 14px", color:C.text, fontSize:14, fontFamily:"inherit", boxSizing:"border-box", outline:"none" };
  const lbl  = { display:"block", color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700, marginBottom:8 };

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val.trim());

  const setFieldError = (field, msg) => setFieldErrors(prev => msg ? { ...prev, [field]: msg } : (({ [field]: _, ...rest }) => rest)(prev));

  const handleBlur = (field) => {
    if (field === "mobile")  { if (mobile && !/^\d{10}$/.test(mobile.trim()))    setFieldError("mobile",  "Enter a valid 10-digit mobile number."); else setFieldError("mobile",  null); }
    if (field === "email")   { const v = (emailRef.current?.value || email).trim(); if (v && !isValidEmail(v)) setFieldError("email", "Enter a valid email address."); else setFieldError("email", null); }
    if (field === "password"){
      if (password && password.length < 8)              setFieldError("password","Password must be at least 8 characters.");
      else if (password && !/[A-Z]/.test(password))    setFieldError("password","Password must contain at least one uppercase letter.");
      else if (password && !/[0-9]/.test(password))    setFieldError("password","Password must contain at least one number.");
      else if (password && !/[^A-Za-z0-9]/.test(password)) setFieldError("password","Password must contain at least one special character.");
      else setFieldError("password",null);
    }
    if (field === "confirm") { if (confirm && confirm !== password)               setFieldError("confirm", "Passwords do not match."); else setFieldError("confirm", null); }
  };

  const borderFor = (field) => fieldErrors[field] ? C.red : C.border;

  const validate = () => {
    if (!/^\d{10}$/.test(mobile.trim()))  return "Enter a valid 10-digit mobile number.";
    const emailVal = (emailRef.current?.value || email).trim();
    if (!isValidEmail(emailVal))          return "Enter a valid email address.";
    if (password.length < 8)                    return "Password must be at least 8 characters.";
    if (!/[A-Z]/.test(password))               return "Password must contain at least one uppercase letter.";
    if (!/[0-9]/.test(password))               return "Password must contain at least one number.";
    if (!/[^A-Za-z0-9]/.test(password))        return "Password must contain at least one special character.";
    if (password !== confirm)                   return "Passwords do not match.";
    return null;
  };

  const handleNext = async () => {
    const err = validate(); if (err) { setError(err); return; }
    setError(""); setLoading(true);
    const finalEmail = (emailRef.current?.value || email).trim().toLowerCase();
    const cleanMobile = mobile.trim();
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-mobile-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: cleanMobile }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Could not send OTP. Please try again."); setLoading(false); return; }
    } catch {
      setError("Could not connect to the server. Please check your connection and try again.");
      setLoading(false);
      return;
    }
    setLoading(false);
    const cleanCode = referralCode.trim().toUpperCase();
    onNext({ mobile: cleanMobile, email: finalEmail, password, marketingOptIn: consentMarketing, ...(cleanCode && { referralCode: cleanCode }) });
  };

  return (
    <div>
      <div style={{ marginBottom:16 }}>
        <label style={lbl}>Mobile number</label>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"11px 12px", color:C.muted, fontSize:14, flexShrink:0 }}>🇮🇳 +91</div>
          <input type="tel" value={mobile}
            onChange={e=>{ setMobile(e.target.value.replace(/\D/g,"").slice(0,10)); setFieldError("mobile", null); }}
            placeholder="98765 43210"
            style={{ ...base, flex:1, borderColor:borderFor("mobile") }}
            onFocus={e=>e.target.style.borderColor=C.accent}
            onBlur={e=>{ e.target.style.borderColor=borderFor("mobile"); handleBlur("mobile"); }} />
        </div>
        {fieldErrors.mobile
          ? <p style={{ color:C.red, fontSize:11, marginTop:4 }}>{fieldErrors.mobile}</p>
          : <p style={{ color:C.muted, fontSize:11, marginTop:4 }}>An OTP will be sent to this number</p>}
      </div>
      <div style={{ marginBottom:16 }}>
        <label style={lbl}>Email address</label>
        <input ref={emailRef} type="email" value={email}
          onChange={e=>{ setEmail(e.target.value); setFieldError("email", null); }}
          placeholder="you@email.com"
          style={{ ...base, borderColor:borderFor("email") }}
          onFocus={e=>e.target.style.borderColor=C.accent}
          onBlur={e=>{ e.target.style.borderColor=borderFor("email"); handleBlur("email"); }} />
        {fieldErrors.email && <p style={{ color:C.red, fontSize:11, marginTop:4 }}>{fieldErrors.email}</p>}
      </div>
      <div style={{ marginBottom:16 }}>
        <label style={lbl}>Password</label>
        <div style={{ position:"relative" }}>
          <input type={showPw?"text":"password"} value={password}
            onChange={e=>{ setPassword(e.target.value); setFieldError("password", null); setFieldError("confirm", null); }}
            placeholder="Min. 8 chars, uppercase, number, symbol"
            style={{ ...base, paddingRight:44, borderColor:borderFor("password") }}
            onFocus={e=>e.target.style.borderColor=C.accent}
            onBlur={e=>{ e.target.style.borderColor=borderFor("password"); handleBlur("password"); }} />
          <button onClick={()=>setShowPw(!showPw)} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:C.muted, cursor:"pointer", fontSize:16 }}>
            {showPw?"🙈":"👁"}
          </button>
        </div>
        {fieldErrors.password
          ? <p style={{ color:C.red, fontSize:11, marginTop:4 }}>{fieldErrors.password}</p>
          : password.length>0&&(
            <div style={{ marginTop:6 }}>
              <div style={{ display:"flex", gap:4 }}>
                {[1,2,3,4].map(i=>{
                  const allRules = /[A-Z]/.test(password)&&/[0-9]/.test(password)&&/[^A-Za-z0-9]/.test(password)&&password.length>=8;
                  const strength = allRules&&password.length>=12?4:allRules&&password.length>=10?3:allRules?2:1;
                  const clr = strength>=4?C.teal:strength>=3?C.accent:strength>=2?C.amber:C.red;
                  return <div key={i} style={{ flex:1, height:3, borderRadius:2, background:i<=strength?clr:C.subtle, transition:"background 0.3s" }} />;
                })}
              </div>
              <span style={{ fontSize:10, color:C.muted, marginTop:3, display:"block" }}>
                {(()=>{const r=/[A-Z]/.test(password)&&/[0-9]/.test(password)&&/[^A-Za-z0-9]/.test(password)&&password.length>=8;return r&&password.length>=12?"Strong — great password!":r&&password.length>=10?"Good password":r?"Acceptable":"Add uppercase, number & symbol";})()}
              </span>
            </div>
          )}
      </div>
      <div style={{ marginBottom:8 }}>
        <label style={lbl}>Confirm password</label>
        <input type="password" value={confirm}
          onChange={e=>{ setConfirm(e.target.value); setFieldError("confirm", null); }}
          placeholder="Re-enter password"
          style={{ ...base, borderColor:borderFor("confirm") }}
          onFocus={e=>e.target.style.borderColor=C.accent}
          onBlur={e=>{ e.target.style.borderColor=borderFor("confirm"); handleBlur("confirm"); }} />
        {fieldErrors.confirm && <p style={{ color:C.red, fontSize:11, marginTop:4 }}>{fieldErrors.confirm}</p>}
      </div>
      {error && <p style={{ color:C.red, fontSize:12, marginTop:8, padding:"8px 12px", background:`${C.red}15`, borderRadius:6 }}>{error}</p>}

      {/* Referral code — optional unless pre-filled from ?r= URL */}
      <div style={{ marginTop:20, padding:"14px 16px", background:`${C.accent}10`, border:`1px solid ${C.accent}30`, borderRadius:10 }}>
        <label style={{ ...lbl, color:C.accent, marginBottom:6 }}>Referral code <span style={{ color:C.muted, fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span></label>
        <input
          type="text"
          value={referralCode}
          onChange={e => !referralLocked && setReferralCode(e.target.value.toUpperCase().slice(0,8))}
          placeholder="e.g. RIK-A3X9"
          readOnly={referralLocked}
          style={{ ...base, fontFamily:"monospace", letterSpacing:"0.12em", fontSize:15, background: referralLocked ? `${C.accent}15` : C.surface, color: referralLocked ? C.accent : C.text, cursor: referralLocked ? "default" : "text" }}
          onFocus={e=>{ if(!referralLocked) e.target.style.borderColor=C.accent; }}
          onBlur={e=>{ e.target.style.borderColor=C.border; }}
        />
        <p style={{ color:C.muted, fontSize:11, marginTop:5 }}>
          {referralLocked
            ? "Referral code applied — you'll earn a ₹20 bonus after completing your first survey."
            : "Have a friend's referral code? Enter it here to earn a ₹20 joining bonus."}
        </p>
      </div>

      {/* DPDP consent checkboxes — required before account creation */}
      <div style={{ marginTop:20, display:"flex", flexDirection:"column", gap:10 }}>
        <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer" }}>
          <input type="checkbox" checked={consentAge} onChange={e=>setConsentAge(e.target.checked)}
            style={{ marginTop:2, flexShrink:0, accentColor:C.accent, width:15, height:15, cursor:"pointer" }} />
          <span style={{ color:C.muted, fontSize:12, lineHeight:1.55 }}>
            I confirm I am <strong style={{ color:C.text }}>18 years of age or older</strong> and agree to the{" "}
            <a href={LEGAL_TOS} target="_blank" rel="noopener" style={{ color:C.accent }} onClick={e=>e.stopPropagation()}>Terms of Service</a>.
          </span>
        </label>
        <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer" }}>
          <input type="checkbox" checked={consentData} onChange={e=>setConsentData(e.target.checked)}
            style={{ marginTop:2, flexShrink:0, accentColor:C.accent, width:15, height:15, cursor:"pointer" }} />
          <span style={{ color:C.muted, fontSize:12, lineHeight:1.55 }}>
            I agree to Rik.ai's{" "}
            <a href={LEGAL_PP} target="_blank" rel="noopener" style={{ color:C.accent }} onClick={e=>e.stopPropagation()}>Privacy Policy</a>
            {" "}— processing my personal data for{" "}
            <strong style={{ color:C.text }}>survey matching and market research purposes</strong>.
            My data will not be sold to third parties and I can request deletion at any time.
          </span>
        </label>
        {/* Marketing opt-in — optional, pre-ticked */}
        <label style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", marginTop:4, background:`${C.teal}08`, border:`1px solid ${C.teal}20`, borderRadius:8, padding:"10px 12px" }}>
          <input type="checkbox" checked={consentMarketing} onChange={e=>setConsentMarketing(e.target.checked)}
            style={{ marginTop:2, flexShrink:0, accentColor:C.teal, width:15, height:15, cursor:"pointer" }} />
          <span style={{ color:C.muted, fontSize:12, lineHeight:1.55 }}>
            Notify me about <strong style={{ color:C.text }}>new surveys, earning opportunities and referral rewards</strong>.
            <span style={{ display:"block", color:C.teal, fontSize:11, marginTop:3 }}>
              Keep this on — participants who receive notifications earn significantly more on average.
            </span>
          </span>
        </label>
      </div>

      <Btn onClick={handleNext} disabled={loading||!mobile||!email||!password||!confirm||!consentAge||!consentData} style={{ width:"100%", marginTop:16 }} size="lg">
        {loading?"Creating account…":"Continue →"}
      </Btn>
      <p style={{ color:C.muted, fontSize:11, textAlign:"center", marginTop:10, lineHeight:1.6 }}>
        The first two consents are required to create an account.{" "}
        <a href={LEGAL_TOS} target="_blank" rel="noopener" style={{ color:C.muted, textDecoration:"underline" }}>Terms</a>
        {" "}·{" "}
        <a href={LEGAL_PP} target="_blank" rel="noopener" style={{ color:C.muted, textDecoration:"underline" }}>Privacy</a>
      </p>
    </div>
  );
}

// ─── STAGE 2: OTP VERIFICATION ────────────────────────────────────────────────
function StageOTP({ mobile, onNext, onBack }) {
  const [otp,      setOtp]      = useState(["","","","","",""]);
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [resendSec,setResendSec]= useState(30);
  const [sent,     setSent]     = useState(true);
  const inputRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendSec <= 0) return;
    const t = setTimeout(() => setResendSec(s=>s-1), 1000);
    return () => clearTimeout(t);
  }, [resendSec]);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp]; next[i] = val;
    setOtp(next);
    if (val && i < 5) inputRefs.current[i+1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key==="Backspace"&&!otp[i]&&i>0) inputRefs.current[i-1]?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length < 6) { setError("Please enter the full 6-digit OTP."); return; }
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-mobile-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile, otp: code }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Invalid OTP. Please try again."); setLoading(false); return; }
      setLoading(false);
      onNext(data.mobileVerificationToken);
    } catch {
      setError("Could not connect to the server. Please check your connection and try again.");
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-mobile-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Could not resend OTP. Please try again."); return; }
      setResendSec(30); setOtp(["","","","","",""]); setError("");
      inputRefs.current[0]?.focus();
    } catch {
      setError("Could not connect to the server. Please check your connection and try again.");
    }
  };

  const boxStyle = { width:44, height:52, textAlign:"center", fontSize:20, fontWeight:700, background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, color:C.text, fontFamily:"inherit", outline:"none", transition:"border-color 0.2s" };

  return (
    <div style={{ textAlign:"center" }}>
      <div style={{ fontSize:48, marginBottom:12 }}>📱</div>
      <h3 style={{ color:C.text, fontSize:18, fontWeight:700, margin:"0 0 8px" }}>Verify your mobile</h3>
      <p style={{ color:C.muted, fontSize:13, marginBottom:28, lineHeight:1.65 }}>
        We've sent a 6-digit OTP to <strong style={{ color:C.text }}>+91 {mobile}</strong>.
      </p>

      {/* OTP boxes */}
      <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:20 }}>
        {otp.map((d,i) => (
          <input key={i} ref={el=>inputRefs.current[i]=el} type="tel" maxLength={1} value={d}
            onChange={e=>handleChange(i,e.target.value)} onKeyDown={e=>handleKeyDown(i,e)}
            style={{ ...boxStyle, borderColor:d?C.accent:C.border }}
            onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=d?C.accent:C.border} />
        ))}
      </div>

      {error && <p style={{ color:C.red, fontSize:12, marginBottom:12 }}>{error}</p>}

      <Btn onClick={handleVerify} disabled={loading||otp.join("").length<6} style={{ width:"100%", marginBottom:16 }} size="lg">
        {loading ? "Verifying…" : "Verify OTP →"}
      </Btn>

      <div style={{ color:C.muted, fontSize:13 }}>
        {resendSec > 0
          ? <>Resend OTP in <strong style={{ color:C.accent }}>{resendSec}s</strong></>
          : <span onClick={handleResend} style={{ color:C.accent, cursor:"pointer", fontWeight:600 }}>Resend OTP</span>
        }
      </div>
      <button onClick={onBack} style={{ background:"none", border:"none", color:C.muted, fontSize:12, cursor:"pointer", marginTop:12, fontFamily:"inherit" }}>
        ← Change mobile number
      </button>
    </div>
  );
}

// ─── STAGE 3: DEMOGRAPHICS ────────────────────────────────────────────────────
function StageDemographics({ onNext, supportedLanguages=["English"] }) {
  const [form, setForm] = useState({ firstName:"", lastName:"", dob:"", gender:"", city:"", cityTier:"", language:"", langOther:"" });
  const [errors, setErrors] = useState({});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const base = { width:"100%", background:C.surface, border:`1px solid ${C.border}`, borderRadius:8, padding:"11px 14px", color:C.text, fontSize:14, fontFamily:"inherit", boxSizing:"border-box", outline:"none" };
  const lbl  = { display:"block", color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:700, marginBottom:8 };

  const handleCityChange = (val) => {
    const found = INDIA_CITIES.find(c=>c.name===val);
    set("city", val); set("cityTier", found ? `Tier ${found.tier}` : "");
  };

  const getAge = (dob) => {
    if (!dob) return null;
    const diff = Date.now() - new Date(dob).getTime();
    return Math.floor(diff / (365.25*24*3600*1000));
  };
  const getAgeBand = (age) => {
    if (!age) return "";
    if (age<18) return "Under 18"; if (age<25) return "18–24"; if (age<35) return "25–34";
    if (age<45) return "35–44"; if (age<55) return "45–54"; if (age<65) return "55–64"; return "65+";
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim())  e.lastName  = "Required";
    if (!form.dob)              e.dob       = "Date of birth is required";
    else { const age=getAge(form.dob); if(age<18) e.dob="You must be at least 18 years old to participate."; else if(age>100) e.dob="Enter a valid date"; }
    if (!form.gender)           e.gender    = "Required";
    if (!form.city)             e.city      = "Required";
    if (!form.language)         e.language  = "Required";
    return e;
  };

  const handleNext = () => {
    const e = validate(); setErrors(e);
    if (Object.keys(e).length) return;
    const age = getAge(form.dob);
    onNext({ ...form, age, ageBand: getAgeBand(age) });
  };

  const age = getAge(form.dob);

  return (
    <div>
      {/* Name row */}
      <div className="reg-name-grid" style={{ marginBottom:0 }}>
        <div style={{ marginBottom:16 }}>
          <label style={lbl}>First name</label>
          <input value={form.firstName} onChange={e=>set("firstName",e.target.value)} placeholder="Jane"
            style={{ ...base, borderColor:errors.firstName?C.red:C.border }}
            onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=errors.firstName?C.red:C.border} />
          {errors.firstName&&<p style={{ color:C.red,fontSize:11,marginTop:3 }}>{errors.firstName}</p>}
        </div>
        <div style={{ marginBottom:16 }}>
          <label style={lbl}>Last name</label>
          <input value={form.lastName} onChange={e=>set("lastName",e.target.value)} placeholder="Smith"
            style={{ ...base, borderColor:errors.lastName?C.red:C.border }}
            onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=errors.lastName?C.red:C.border} />
          {errors.lastName&&<p style={{ color:C.red,fontSize:11,marginTop:3 }}>{errors.lastName}</p>}
        </div>
      </div>

      {/* DOB + computed age band */}
      <div style={{ marginBottom:16 }}>
        <label style={lbl}>Date of birth</label>
        <div className="reg-dob-row">
          <input type="date" value={form.dob} onChange={e=>set("dob",e.target.value)}
            style={{ ...base, flex:1, borderColor:errors.dob?C.red:C.border, colorScheme:"dark" }}
            onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=errors.dob?C.red:C.border} />
          {age && <div style={{ background:`${C.accent}15`, border:`1px solid ${C.accent}40`, borderRadius:8, padding:"8px 14px", fontSize:12, color:C.accent, whiteSpace:"nowrap" }}>
            Age band: <strong>{getAgeBand(age)}</strong>
          </div>}
        </div>
        {errors.dob&&<p style={{ color:C.red,fontSize:11,marginTop:3 }}>{errors.dob}</p>}
      </div>

      {/* Gender */}
      <div style={{ marginBottom:16 }}>
        <label style={lbl}>Gender</label>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {["Male","Female","Non-binary","Prefer not to say"].map(g=>{
            const sel=form.gender===g;
            return <button key={g} onClick={()=>set("gender",g)}
              style={{ padding:"8px 18px", borderRadius:20, fontSize:13, fontWeight:sel?600:400, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s",
                background:sel?C.accent:C.surface, color:sel?"#fff":C.muted, border:`1px solid ${sel?C.accent:C.border}` }}>
              {g}
            </button>;
          })}
        </div>
        {errors.gender&&<p style={{ color:C.red,fontSize:11,marginTop:3 }}>{errors.gender}</p>}
      </div>

      {/* City */}
      <div style={{ marginBottom:16 }}>
        <label style={lbl}>City</label>
        <div className="reg-city-row">
          <div style={{ position:"relative", flex:1 }}>
            <select value={form.city} onChange={e=>handleCityChange(e.target.value)}
              style={{ ...base, paddingRight:36, cursor:"pointer", appearance:"none", borderColor:errors.city?C.red:C.border }}
              onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=errors.city?C.red:C.border}>
              <option value="">Select your city…</option>
              {INDIA_CITIES.map(c=><option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", color:C.muted, pointerEvents:"none", fontSize:12 }}>▾</span>
          </div>
        </div>
        {errors.city&&<p style={{ color:C.red,fontSize:11,marginTop:3 }}>{errors.city}</p>}
      </div>

      {/* Language */}
      <div style={{ marginBottom:8 }}>
        <label style={lbl}>Preferred language</label>
        <div style={{ position:"relative" }}>
          <select value={form.language} onChange={e=>set("language",e.target.value)}
            style={{ ...base, paddingRight:36, cursor:"pointer", appearance:"none", borderColor:errors.language?C.red:C.border }}
            onFocus={e=>e.target.style.borderColor=C.accent} onBlur={e=>e.target.style.borderColor=errors.language?C.red:C.border}>
            <option value="">Select language…</option>
            {supportedLanguages.map(l=><option key={l} value={l}>{l}</option>)}
          </select>
          <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", color:C.muted, pointerEvents:"none", fontSize:12 }}>▾</span>
        </div>
        {errors.language&&<p style={{ color:C.red,fontSize:11,marginTop:3 }}>{errors.language}</p>}
      </div>

      <Btn onClick={handleNext} style={{ width:"100%", marginTop:24 }} size="lg">Save & Continue →</Btn>
    </div>
  );
}

// ─── STAGE 4: PROFILE SURVEY (5 sections, progressive wizard) ─────────────────
function StageProfileSurvey({ onNext }) {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [answers,    setAnswers]    = useState({});
  const [errors,     setErrors]     = useState({});
  const tabRefs = useRef([]);
  useEffect(() => {
    tabRefs.current[sectionIdx]?.scrollIntoView({ behavior:"smooth", inline:"nearest", block:"nearest" });
  }, [sectionIdx]);

  const section = PROFILE_SECTIONS[sectionIdx];
  const isLast  = sectionIdx === PROFILE_SECTIONS.length - 1;
  const totalPct = Math.round((sectionIdx / PROFILE_SECTIONS.length) * 100);

  const setAnswer = (key, val) => {
    setAnswers(a => ({...a, [key]: val}));
    setErrors(e => { const n={...e}; delete n[key]; return n; });
  };

  const validateSection = () => {
    const e = {};
    for (const f of section.fields) {
      if (!f.required) continue;
      const val = answers[f.key];
      if (f.type==="multiselect") { if (!val||val.length===0) e[f.key]="Please select at least one option."; }
      else { if (!val) e[f.key]="This field is required."; }
    }
    return e;
  };

  const handleNext = () => {
    const e = validateSection(); setErrors(e);
    if (Object.keys(e).length) return;
    if (isLast) { onNext(answers); return; }
    setSectionIdx(i=>i+1);
    setErrors({});
    window.scrollTo({top:0,behavior:"smooth"});
  };

  return (
    <div>
      {/* Section progress bar */}
      <div style={{ marginBottom:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
          <span style={{ fontSize:12, color:C.muted }}>Section {sectionIdx+1} of {PROFILE_SECTIONS.length}</span>
          <span style={{ fontSize:12, color:C.accent, fontWeight:600 }}>{totalPct}% complete</span>
        </div>
        <div style={{ background:C.subtle, borderRadius:99, height:4, overflow:"hidden" }}>
          <div style={{ width:`${totalPct}%`, height:"100%", background:`linear-gradient(90deg,${C.accent},${C.teal})`, borderRadius:99, transition:"width 0.5s ease" }} />
        </div>
      </div>

      {/* Section tabs */}
      <div style={{ display:"flex", gap:6, overflowX:"auto", marginBottom:24, scrollbarWidth:"none", msOverflowStyle:"none" }}>
        {PROFILE_SECTIONS.map((s,i)=>{
          const done   = i < sectionIdx;
          const active = i === sectionIdx;
          return (
            <div key={s.id} ref={el => tabRefs.current[i] = el} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 10px", borderRadius:20, fontSize:11, fontWeight:active?700:400, whiteSpace:"nowrap", flexShrink:0,
              background:done?`${C.teal}15`:active?`${C.accent}15`:C.surface,
              color:done?C.teal:active?C.accent:C.muted,
              border:`1px solid ${done?C.teal+"50":active?C.accent+"50":C.border}` }}>
              {done?"✓ ":""}{s.icon} {s.title.split(" ")[0]}
            </div>
          );
        })}
      </div>

      {/* Section header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
        <div style={{ width:44, height:44, borderRadius:12, background:`${C.accent}15`, border:`1px solid ${C.accent}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>{section.icon}</div>
        <div>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:C.text }}>{section.title}</h3>
          <p style={{ margin:0, fontSize:12, color:C.muted }}>{section.subtitle}</p>
        </div>
      </div>

      {/* Fields */}
      {section.fields.map(f=>(
        <FieldInput key={f.key} field={f} value={answers[f.key]} onChange={v=>setAnswer(f.key,v)} error={errors[f.key]} />
      ))}

      {/* Actions */}
      <div style={{ display:"flex", gap:10, marginTop:20 }}>
        {sectionIdx > 0 && (
          <Btn variant="ghost" onClick={()=>setSectionIdx(i=>i-1)} style={{ flex:"0 0 auto" }}>← Back</Btn>
        )}
        <Btn onClick={handleNext} style={{ flex:1 }} size="lg">
          {isLast ? "Complete Profile →" : `Next: ${PROFILE_SECTIONS[sectionIdx+1]?.title.split(" ")[0]} →`}
        </Btn>
      </div>
    </div>
  );
}

// ─── STAGE 5: DEVICE & IDENTITY VERIFICATION ─────────────────────────────────
const VERIFICATION_QUESTIONS = [
  { id:"fullName",   prompt:"Please state your full name clearly.",              hint:"Speak naturally — say your first and last name" },
  { id:"location",   prompt:"What city are you currently calling in from?",      hint:"Just the city name is fine, e.g. Mumbai or Bengaluru" },
  { id:"occupation", prompt:"Briefly describe your current role or occupation.", hint:'e.g. "I\'m a software engineer" or "I run a small business"' },
];

// Captures a 64×36 canvas frame and returns avg per-pixel absolute difference
// vs the previous frame stored in prevFrameRef.
// Near 0 = static image / empty room. Live person at rest ≈ 2–6. Speaking ≈ 8–20+.
function measureMotion(video, prevFrameRef) {
  if (!video || video.readyState < 2) return 0;
  const W = 64, H = 36;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(video, 0, 0, W, H);
  const curr = ctx.getImageData(0, 0, W, H).data;
  const prev = prevFrameRef.current;
  prevFrameRef.current = curr.slice(); // keep a copy for next comparison
  if (!prev) return 0;
  let sum = 0;
  for (let i = 0; i < curr.length; i += 4) {
    sum += Math.abs(curr[i]-prev[i]) + Math.abs(curr[i+1]-prev[i+1]) + Math.abs(curr[i+2]-prev[i+2]);
  }
  return sum / (W * H * 3); // avg diff per pixel per RGB channel
}

// Draws a 64×36 frame from the video onto a canvas and returns the average
// perceived brightness (0–255). A covered/blocked camera returns < 10.
function sampleBrightness(video) {
  if (!video || video.readyState < 2) return 255; // can't sample yet → assume ok
  const canvas = document.createElement("canvas");
  canvas.width = 64; canvas.height = 36;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(video, 0, 0, 64, 36);
  const { data } = ctx.getImageData(0, 0, 64, 36);
  let sum = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }
  return sum / (64 * 36); // avg brightness across all pixels
}

function StageVerification({ onNext, submitError, submitLoading }) {
  const [phase,     setPhase]     = useState("camera");  // camera | call | done
  const [camStatus, setCamStatus] = useState("idle");    // idle | checking | ok | error | blocked | absent
  const [qIdx,      setQIdx]      = useState(0);
  const [answers,   setAnswers]   = useState({});
  const [micActive, setMicActive] = useState(false);
  const [liveText,  setLiveText]  = useState("");
  const [elapsed,   setElapsed]   = useState(0);
  const [faceOk,    setFaceOk]    = useState(null);  // null=warming up | true=live | false=no one detected

  const videoRef      = useRef(null);
  const streamRef     = useRef(null);
  const recogRef      = useRef(null);
  const timerRef      = useRef(null);
  const brightRef     = useRef(null);  // interval: covered-camera brightness check
  const faceCheckRef  = useRef(null);  // interval: liveness / motion detection
  const prevFrameRef  = useRef(null);  // previous frame pixel data for motion diff
  const motionBuf     = useRef([]);    // rolling window of motion scores (~3s)
  const absentCount   = useRef(0);     // consecutive frames with motion below threshold
  const capturedRef   = useRef("");
  const answersRef    = useRef({});

  const TOTAL_Q = VERIFICATION_QUESTIONS.length;

  // Re-attach camera stream whenever video element remounts (phase transitions)
  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  });

  const startCamera = async () => {
    setCamStatus("checking");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video:true, audio:false });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        // Wait until the video element has at least one rendered frame
        await new Promise(resolve => {
          if (video.readyState >= 2) resolve();
          else video.addEventListener("loadeddata", resolve, { once:true });
        });
      }
      // Give the camera auto-exposure a moment to settle before sampling brightness
      await new Promise(r => setTimeout(r, 800));
      const brightness = sampleBrightness(videoRef.current);
      if (brightness < 10) {
        // Frame is near-black — camera is physically covered or completely dark
        stream.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setCamStatus("blocked");
      } else {
        setCamStatus("ok");
      }
    } catch {
      setCamStatus("error");
    }
  };

  const startCall = () => {
    setPhase("call");
    setQIdx(0);
    setElapsed(0);
    setAnswers({});
    answersRef.current = {};
    // Reset liveness tracking for this call attempt
    prevFrameRef.current = null;
    motionBuf.current    = [];
    absentCount.current  = 0;
    setFaceOk(null);

    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);

    // ── Covered-camera check (brightness) every 2s ───────────────────────────
    brightRef.current = setInterval(() => {
      if (sampleBrightness(videoRef.current) < 10) {
        clearInterval(timerRef.current);
        clearInterval(brightRef.current);
        clearInterval(faceCheckRef.current);
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        recogRef.current?.stop();
        setFaceOk(null);
        setCamStatus("blocked");
        setPhase("camera");
      }
    }, 2000);

    // ── Liveness monitor (frame differencing) every 300ms ───────────────────
    // A still photo / empty room scores ~0–1. A live person at rest scores ~2–6.
    // If motion stays below 1.5 for ~6 consecutive seconds → abort the call.
    faceCheckRef.current = setInterval(() => {
      const score = measureMotion(videoRef.current, prevFrameRef);
      motionBuf.current = [...motionBuf.current.slice(-9), score];
      if (motionBuf.current.length < 5) return; // wait for buffer to warm up
      const avg = motionBuf.current.reduce((a, b) => a + b, 0) / motionBuf.current.length;
      const alive = avg > 1.5;
      setFaceOk(alive);
      if (!alive) {
        absentCount.current += 1;
        if (absentCount.current >= 20) { // 20 × 300ms = 6s continuous absence
          clearInterval(timerRef.current);
          clearInterval(brightRef.current);
          clearInterval(faceCheckRef.current);
          streamRef.current?.getTracks().forEach(t => t.stop());
          streamRef.current = null;
          recogRef.current?.stop();
          setFaceOk(null);
          setCamStatus("absent");
          setPhase("camera");
        }
      } else {
        absentCount.current = 0;
      }
    }, 300);
  };

  const startListening = (idx) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = false; rec.interimResults = true; rec.lang = "en-US";
    capturedRef.current = "";
    let live = "";
    rec.onstart  = () => setMicActive(true);
    rec.onresult = e => {
      let t = ""; for (let i = 0; i < e.results.length; i++) t += e.results[i][0].transcript;
      live = t; setLiveText(t);
      if (e.results[e.results.length - 1].isFinal) capturedRef.current = t;
    };
    rec.onend = () => {
      setMicActive(false);
      const captured = capturedRef.current || live;
      const key = VERIFICATION_QUESTIONS[idx].id;
      const updated = { ...answersRef.current, [key]: captured };
      answersRef.current = updated;
      setAnswers({ ...updated });
      setLiveText("");
      capturedRef.current = "";
      const next = idx + 1;
      if (next < TOTAL_Q) { setQIdx(next); }
      else { clearInterval(timerRef.current); setPhase("done"); }
    };
    rec.onerror = () => setMicActive(false);
    recogRef.current = rec;
    try { rec.start(); } catch { setMicActive(false); }
  };

  const stopListening = () => recogRef.current?.stop();

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    recogRef.current?.stop();
    clearInterval(timerRef.current);
    clearInterval(brightRef.current);
    clearInterval(faceCheckRef.current);
  }, []);

  const timeLeft = Math.max(0, 60 - elapsed);
  const tMM = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const tSS = String(timeLeft % 60).padStart(2, "0");

  return (
    <div>
      <style>{`
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes micPulse{0%,100%{transform:scale(1);box-shadow:0 0 22px ${C.accent}70}50%{transform:scale(1.07);box-shadow:0 0 34px ${C.accent}aa}}
        @keyframes micPulseRed{0%,100%{transform:scale(1);box-shadow:0 0 22px ${C.red}70}50%{transform:scale(1.07);box-shadow:0 0 34px ${C.red}aa}}
      `}</style>

      {/* ── PHASE: CAMERA SETUP ─────────────────────────────────────────────── */}
      {phase === "camera" && (<>
        <p style={{ color:C.muted, fontSize:13, marginBottom:20, lineHeight:1.65 }}>
          We'll run a quick <strong style={{ color:C.text }}>~1 minute verification call</strong> to check your devices and confirm a few details before your first interview.
        </p>

        {/* Camera card */}
        <div style={{ background:C.surface, border:`1px solid ${camStatus==="ok"?C.teal:camStatus==="error"||camStatus==="blocked"||camStatus==="absent"?C.red:C.border}`, borderRadius:12, padding:16, marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:camStatus!=="idle"?12:0 }}>
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <span style={{ fontSize:22 }}>📷</span>
              <div>
                <div style={{ color:C.text, fontSize:13, fontWeight:600 }}>Camera</div>
                <div style={{ color:C.muted, fontSize:11 }}>
                  {camStatus==="ok"      ? "Looking good!" :
                   camStatus==="blocked" ? "Camera appears covered or too dark — uncover it and retry" :
                   camStatus==="absent"  ? "No one detected in frame — sit in front of the camera and retry" :
                   camStatus==="error"   ? "Could not access camera — allow permission and retry" :
                   camStatus==="checking"? "Starting camera…" : "Not yet started"}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              {camStatus==="ok"                                                       && <span style={{ color:C.teal, fontSize:20 }}>✅</span>}
              {camStatus==="idle"                                                     && <Btn size="sm" onClick={startCamera}>Enable Camera</Btn>}
              {(camStatus==="error"||camStatus==="blocked"||camStatus==="absent")     && <Btn size="sm" variant="outline" onClick={startCamera}>Retry</Btn>}
            </div>
          </div>
          {(camStatus==="ok" || camStatus==="checking") && (
            <div style={{ borderRadius:10, overflow:"hidden", background:"#000", aspectRatio:"16/9", maxHeight:160, position:"relative" }}>
              <video ref={videoRef} autoPlay muted playsInline style={{ width:"100%", height:"100%", objectFit:"cover", transform:"scaleX(-1)" }} />
              {camStatus==="checking" && (
                <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.55)", color:C.muted, fontSize:13 }}>Starting…</div>
              )}
            </div>
          )}
        </div>

        {/* What we'll ask preview */}
        <div style={{ background:`${C.accent}08`, border:`1px solid ${C.accent}25`, borderRadius:12, padding:"14px 18px", marginBottom:20 }}>
          <div style={{ color:C.accent, fontWeight:700, fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}>What we'll ask during the call</div>
          {VERIFICATION_QUESTIONS.map((q, i) => (
            <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:i < TOTAL_Q-1 ? 10 : 0 }}>
              <span style={{ background:C.accent, color:"#fff", borderRadius:"50%", width:18, height:18, fontSize:10, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>{i+1}</span>
              <span style={{ color:C.muted, fontSize:12, lineHeight:1.5 }}>{q.prompt}</span>
            </div>
          ))}
        </div>

        <Btn onClick={startCall} disabled={camStatus !== "ok"} style={{ width:"100%" }} size="lg">
          {camStatus === "ok" ? "Start Verification Call →" : "Enable camera first"}
        </Btn>
        {camStatus !== "ok" && (
          <p style={{ color:C.amber, fontSize:11, textAlign:"center", marginTop:8 }}>⚠ Camera access is required to begin</p>
        )}
      </>)}

      {/* ── PHASE: VERIFICATION CALL ────────────────────────────────────────── */}
      {phase === "call" && (()=>{
        const q = VERIFICATION_QUESTIONS[qIdx];
        return (<>
          {/* Progress row */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontSize:11, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em" }}>
              Question {qIdx + 1} of {TOTAL_Q}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontSize:14, fontFamily:"monospace", fontWeight:700, color:timeLeft<=15?C.red:timeLeft<=30?C.amber:C.teal }}>
                {tMM}:{tSS}
              </span>
              <span style={{ fontSize:11, color:C.muted }}>remaining</span>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ marginBottom:16, background:C.subtle, borderRadius:99, height:4, overflow:"hidden" }}>
            <div style={{ width:`${(qIdx / TOTAL_Q) * 100}%`, height:"100%", background:`linear-gradient(90deg,${C.accent},${C.teal})`, borderRadius:99, transition:"width 0.4s ease" }} />
          </div>

          {/* Camera — compact thumbnail with liveness badge overlay */}
          <div style={{ borderRadius:10, overflow:"hidden", background:"#000", aspectRatio:"16/9", maxHeight:110, marginBottom:8, border:`1px solid ${C.border}`, position:"relative" }}>
            <video ref={videoRef} autoPlay muted playsInline style={{ width:"100%", height:"100%", objectFit:"cover", transform:"scaleX(-1)" }} />
            <div style={{ position:"absolute", top:6, right:6, display:"flex", alignItems:"center", gap:4, background:"rgba(0,0,0,0.62)", borderRadius:12, padding:"3px 8px" }}>
              <span style={{ width:6, height:6, borderRadius:"50%", flexShrink:0, background: faceOk === null ? C.amber : faceOk ? C.teal : C.red }} />
              <span style={{ fontSize:10, color:"#fff", fontWeight:600 }}>
                {faceOk === null ? "Checking…" : faceOk ? "Person detected" : "No one detected"}
              </span>
            </div>
          </div>

          {/* No-person warning */}
          {faceOk === false && (
            <div style={{ display:"flex", alignItems:"center", gap:8, background:`${C.red}12`, border:`1px solid ${C.red}30`, borderRadius:8, padding:"8px 12px", marginBottom:12 }}>
              <span style={{ color:C.red, flexShrink:0 }}>⚠</span>
              <span style={{ color:C.red, fontSize:12, lineHeight:1.4 }}>We can't detect anyone in frame — position yourself clearly in front of the camera to answer</span>
            </div>
          )}

          {/* Current question */}
          <div style={{ background:C.surface, border:`1px solid ${C.accent}40`, borderRadius:14, padding:"18px 20px", marginBottom:14 }}>
            <div style={{ color:C.accent, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", marginBottom:8 }}>
              Verification Question {qIdx + 1}
            </div>
            <div style={{ color:C.text, fontSize:16, fontWeight:700, lineHeight:1.55, marginBottom:5 }}>{q.prompt}</div>
            <div style={{ color:C.muted, fontSize:11 }}>{q.hint}</div>
          </div>

          {/* Live transcript */}
          {(micActive || liveText) && (
            <div style={{ background:`${C.teal}10`, border:`1px solid ${C.teal}30`, borderRadius:10, padding:"11px 15px", marginBottom:14, minHeight:42 }}>
              <div style={{ color:C.teal, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:3 }}>Capturing…</div>
              <div style={{ color:C.text, fontSize:13, fontStyle:"italic" }}>{liveText || "Start speaking…"}</div>
            </div>
          )}

          {/* Previously answered */}
          {qIdx > 0 && (
            <div style={{ marginBottom:14 }}>
              {VERIFICATION_QUESTIONS.slice(0, qIdx).map((pq, i) => (
                <div key={i} style={{ display:"flex", gap:10, padding:"8px 12px", background:`${C.teal}08`, border:`1px solid ${C.teal}20`, borderRadius:8, marginBottom:6 }}>
                  <span style={{ color:C.teal, fontSize:11, fontWeight:700, minWidth:24 }}>Q{i+1}</span>
                  <div>
                    <div style={{ color:C.muted, fontSize:10, marginBottom:2 }}>{pq.prompt}</div>
                    <div style={{ color:C.text, fontSize:12, fontStyle:"italic" }}>"{answers[pq.id] || "—"}"</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Mic button */}
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8, marginTop:6 }}>
            <button
              onClick={micActive ? stopListening : () => startListening(qIdx)}
              disabled={faceOk === false}
              style={{
                width:68, height:68, borderRadius:"50%", border:"none",
                cursor: faceOk === false ? "not-allowed" : "pointer",
                fontSize:26, display:"flex", alignItems:"center", justifyContent:"center",
                background: faceOk === false ? C.subtle : micActive ? C.red : C.accent,
                animation: faceOk === false ? "none" : micActive ? "micPulseRed 1.2s infinite" : "micPulse 2s infinite",
                opacity: faceOk === false ? 0.45 : 1,
              }}
            >{micActive ? "⏹" : "🎤"}</button>
            <span style={{ color: faceOk === false ? C.red : C.muted, fontSize:11 }}>
              {faceOk === false ? "Face not visible — cannot record answer" : micActive ? "Tap ⏹ when finished answering" : "Tap 🎤 to answer"}
            </span>
          </div>
        </>);
      })()}

      {/* ── PHASE: DONE ─────────────────────────────────────────────────────── */}
      {phase === "done" && (<>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:44, marginBottom:10 }}>🎉</div>
          <div style={{ color:C.teal, fontWeight:800, fontSize:18, marginBottom:4 }}>Verification Complete!</div>
          <div style={{ color:C.muted, fontSize:13 }}>Your camera, microphone, and identity have been verified.</div>
        </div>

        {/* Response summary */}
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:"16px 20px", marginBottom:20 }}>
          <div style={{ color:C.muted, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>Your Responses</div>
          {VERIFICATION_QUESTIONS.map((q, i) => (
            <div key={i} style={{
              paddingBottom:i < TOTAL_Q-1 ? 12 : 0,
              marginBottom: i < TOTAL_Q-1 ? 12 : 0,
              borderBottom: i < TOTAL_Q-1 ? `1px solid ${C.border}` : "none",
            }}>
              <div style={{ color:C.muted, fontSize:11, marginBottom:4 }}>{q.prompt}</div>
              <div style={{ color:C.text, fontSize:14, fontWeight:600 }}>"{answers[q.id] || "—"}"</div>
            </div>
          ))}
        </div>

        <div style={{ display:"flex", gap:10, alignItems:"center", background:`${C.teal}10`, border:`1px solid ${C.teal}30`, borderRadius:10, padding:"12px 16px", marginBottom:20 }}>
          <span>✅</span>
          <span style={{ color:C.teal, fontSize:12 }}>Camera and microphone verified — you're all set for interviews</span>
        </div>

        {submitError && <p style={{ color:C.red, fontSize:13, textAlign:"center", marginBottom:8 }}>{submitError}</p>}
        <Btn onClick={onNext} disabled={submitLoading} style={{ width:"100%" }} size="lg">{submitLoading ? "Creating account…" : "Finish Setup → Go to Dashboard"}</Btn>
      </>)}
    </div>
  );
}

// ─── STAGE 6: VERIFY EMAIL ────────────────────────────────────────────────────
function StageVerifyEmail({ email, onBackToLogin }) {
  const [cooldown,    setCooldown]    = useState(0);
  const [resendState, setResendState] = useState("idle"); // idle | loading | done | error

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const handleResend = async () => {
    setResendState("loading");
    try {
      const r = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (r.ok) { setResendState("done"); setCooldown(60); }
      else { setResendState("error"); }
    } catch { setResendState("error"); }
  };

  return (
    <div style={{ textAlign:"center", padding:"8px 0 4px" }}>
      <div style={{ width:64, height:64, borderRadius:"50%", background:C.tealGlow, border:`2px solid ${C.teal}40`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontSize:28 }}>✉️</div>
      <h3 style={{ margin:"0 0 10px", fontSize:20, fontWeight:800, color:C.text }}>Check your inbox</h3>
      <p style={{ margin:"0 0 6px", color:C.muted, fontSize:14, lineHeight:1.6 }}>
        We've sent a verification link to
      </p>
      <p style={{ margin:"0 0 24px", color:C.text, fontSize:14, fontWeight:600 }}>{email}</p>
      <p style={{ margin:"0 0 28px", color:C.muted, fontSize:13, lineHeight:1.6 }}>
        Click the link in that email to activate your account. The link expires in 24 hours.
      </p>

      {resendState === "done" && (
        <div style={{ background:C.tealGlow, border:`1px solid ${C.teal}40`, borderRadius:8, padding:"10px 16px", marginBottom:16, fontSize:13, color:C.teal }}>
          Verification email resent. Check your inbox.
        </div>
      )}
      {resendState === "error" && (
        <div style={{ background:"#FF475720", border:"1px solid #FF475740", borderRadius:8, padding:"10px 16px", marginBottom:16, fontSize:13, color:C.red }}>
          Could not resend. Please try again.
        </div>
      )}

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        <button
          onClick={handleResend}
          disabled={resendState === "loading" || cooldown > 0}
          style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:10, padding:"11px 20px", color: cooldown > 0 ? C.muted : C.accent, fontSize:14, cursor: cooldown > 0 || resendState === "loading" ? "default" : "pointer", fontFamily:"inherit", transition:"all 0.2s" }}
        >
          {resendState === "loading" ? "Sending…" : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend verification email"}
        </button>
        <button
          onClick={onBackToLogin}
          style={{ background:"transparent", border:"none", color:C.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit", padding:"8px 0" }}
        >
          ← Back to Sign In
        </button>
      </div>
    </div>
  );
}

// ─── MAIN REGISTRATION FLOW ───────────────────────────────────────────────────
const STEPS = ["Account","Verify","Profile","Demographics","Device"];

export default function RegistrationFlow({ onComplete, onBackToLogin, campaignId, referralCode: referralCodeProp, supportedLanguages=["English"] }) {
  const [step,        setStep]        = useState(0);
  const [regError,    setRegError]    = useState("");
  const [regLoading,  setRegLoading]  = useState(false);
  // Accumulated data across all steps; seed campaignId and referralCode from URL if present
  const [regData,  setRegData]  = useState({
    ...(campaignId       && { campaignId }),
    ...(referralCodeProp && { referralCode: referralCodeProp }),
  });

  const merge = (data) => setRegData(d => ({...d, ...data}));

  const handleAccountDone = (data) => {
    // data may include referralCode entered in step 1
    merge(data);
    setStep(1);
  };
  const handleOTPDone     = (mobileVerificationToken) => { merge({ mobileVerificationToken }); setStep(2); };
  const handleProfileDone = (data) => { merge({ profileAnswers: data }); setStep(3); };
  const handleDemosDone   = (data) => { merge({ demographics: data }); setStep(4); };
  const handleDeviceDone  = async () => {
    const name = `${regData.demographics?.firstName||""} ${regData.demographics?.lastName||""}`.trim();
    setRegError(""); setRegLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email:           regData.email,
          password:        regData.password,
          name,
          mobile:          regData.mobile,
          mobileVerificationToken: regData.mobileVerificationToken || null,
          demographics:    regData.demographics  || null,
          profile:         regData.profileAnswers || null,
          campaignId:      regData.campaignId    || null,
          referralCode:    regData.referralCode  || null,
          marketingOptIn:  regData.marketingOptIn !== false, // default true
        }),
      });
      const data = await res.json();
      if (!res.ok) { setRegError(data.error || "Registration failed. Please try again."); setRegLoading(false); return; }
      setStep(5); // → StageVerifyEmail
    } catch (err) {
      setRegError("Could not connect to the server. Please check your connection and try again.");
    }
    setRegLoading(false);
  };

  const STEP_TITLES = [
    { title:"Create Your Account",     sub:"Set up your Rik.ai participant account" },
    { title:"Verify Mobile Number",    sub:"One-time verification to keep your account secure" },
    { title:"Tell Us About Yourself",  sub:"Helps us match you to surveys you qualify for" },
    { title:"Your Background",         sub:"Basic details so researchers can find the right participants" },
    { title:"Device Check",            sub:"Make sure your camera and mic are ready for interviews" },
  ];

  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"'Poppins','Segoe UI',Arial,sans-serif", color:C.text }}>
      <style>{`*{box-sizing:border-box;} @keyframes pulseDot{0%,100%{opacity:1}50%{opacity:0.3}} @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}} input,select{outline:none;} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
.reg-content{padding:32px 24px 60px;} @media(max-width:480px){.reg-content{padding:20px 16px 48px;}}
.reg-card{padding:28px 28px 24px;} @media(max-width:480px){.reg-card{padding:20px 16px 20px;}}
.reg-step-connector{width:48px;height:2px;margin:0 4px;margin-bottom:16px;transition:background 0.3s;} @media(max-width:480px){.reg-step-connector{width:16px;}}
.reg-step-label{font-size:10px;white-space:nowrap;} @media(max-width:480px){.reg-step-label{display:none;}}
.reg-name-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;} @media(max-width:480px){.reg-name-grid{grid-template-columns:1fr;gap:0;}}
.reg-dob-row{display:flex;gap:10px;align-items:center;} @media(max-width:480px){.reg-dob-row{flex-wrap:wrap;}}
.reg-city-row{display:flex;gap:10px;align-items:center;} @media(max-width:480px){.reg-city-row{flex-wrap:wrap;}}
`}</style>

      {/* Header */}
      <header style={{ borderBottom:`1px solid ${C.border}`, padding:"0 24px", display:"flex", alignItems:"center", justifyContent:"space-between", height:56 }}>
        <div style={{ display:"flex", alignItems:"flex-end", gap:10 }}>
          <img src="/rik-logo.png" alt="Rik.ai" style={{ height:36, width:"auto" }} onError={e=>{e.currentTarget.style.display="none";}} />
          <span style={{ lineHeight:1 }}>
            <span style={{ fontWeight:700, fontSize:26, fontFamily:"'Poppins',sans-serif", color:C.text, letterSpacing:"-1px" }}>Rik</span><span style={{ fontWeight:300, fontSize:26, fontFamily:"'Poppins',sans-serif", letterSpacing:"-1px", background:"linear-gradient(135deg,#8B5CF6,#3B82F6,#22D3EE)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", paddingLeft:3 }}>.ai</span>
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={onBackToLogin} style={{ background:"none", border:"none", color:C.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
            ← Back to Sign In
          </button>
        </div>
      </header>

      <div className="reg-content" style={{ maxWidth:560, margin:"0 auto", animation:"fadeUp 0.35s ease" }}>

        {/* Step indicator — hidden after device step */}
        {step < 5 && <StepIndicator steps={STEPS} current={step} />}

        {/* Step title — hidden after device step */}
        {step < 5 && (
          <div style={{ textAlign:"center", marginBottom:28 }}>
            <h2 style={{ margin:"0 0 6px", fontSize:22, fontWeight:800, color:C.text }}>{STEP_TITLES[step].title}</h2>
            <p style={{ margin:0, color:C.muted, fontSize:13 }}>{STEP_TITLES[step].sub}</p>
          </div>
        )}

        {/* Step card */}
        <div className="reg-card" style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16 }}>
          {step===0 && <StageAccount    onNext={handleAccountDone} prefillReferralCode={regData.referralCode || null} />}
          {step===1 && <StageOTP        mobile={regData.mobile} onNext={handleOTPDone} onBack={()=>setStep(0)} />}
          {step===2 && <StageProfileSurvey onNext={handleProfileDone} />}
          {step===3 && <StageDemographics  onNext={handleDemosDone} supportedLanguages={supportedLanguages} />}
          {step===4 && <StageVerification  onNext={handleDeviceDone} submitError={regError} submitLoading={regLoading} />}
          {step===5 && <StageVerifyEmail   email={regData.email} onBackToLogin={onBackToLogin} />}
        </div>

        {/* Footer note */}
        <p style={{ textAlign:"center", color:C.muted, fontSize:11, marginTop:20, lineHeight:1.65 }}>
          Your data is stored securely and used only to match you to relevant research surveys.<br/>
          We never sell your personal data to third parties.
        </p>
      </div>
    </div>
  );
}
